import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { track } from '../../lib/telemetry';
import useFavoritesAutoSuggest from '../../hooks/useFavoritesAutoSuggest';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import { apiFetch } from '../../fetchInterceptor';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import WorkspaceContextPill from '../WorkspaceContextPill';
import { NotificationBell } from '../notifications/NotificationBell';
import HelpMenu from '../HelpMenu';
import ProductTourBanner from '../ProductTourBanner';
import { ImpersonationBanner } from '../ImpersonationBanner';
import { AdminViewBanner } from '../AdminViewBanner';
import { deferGatingForSetup } from '../../lib/setupGate';
import { trackFunnel } from '../../lib/funnelTracker';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  BookOpen,
  LayoutDashboard,
  FileText,
  FolderTree,
  BookMarked,
  BarChart3,
  Settings,
  History,
  Moon,
  Sun,
  Monitor,
  LogOut,
  ChevronDown,
  ChevronRight,
  Plus,
  Building2,
  Menu,
  X,
  Landmark,
  Users,
  Receipt,
  Upload,
  Sparkles,
  Zap,
  Shield,
  Search,
  Command,
  Rocket,
  Home,
  CreditCard,
  PiggyBank,
  TrendingUp,
  HelpCircle,
  Bell,
  BrainCircuit,
  ArrowRightLeft,
  PhoneCall,
  MessageCircle,
  MessageSquare,
  ClipboardList,
  Timer,
  Repeat,
  Inbox,
  GraduationCap,
  Map as MapIcon,
  ScanLine,
  Brain,
  Trophy as TrophyIcon,
  Medal as MedalIcon,
  Scale,
  Megaphone,
  Handshake,
  Workflow,
  BookOpen as KnowledgeIcon,
  Briefcase as OperationsIcon,
  Compass,
  Star,
  StarOff,
  FileSignature,
  ShieldCheck,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { AIBuckGlobal } from '../ai-buck';
import SetupWizard from '../SetupWizard';
import { TourLauncher } from '../TourLauncher';
import HeaderLimitPill from '../HeaderLimitPill';
import { CBLogo } from '../CBLogo';
import QuickLinks from '../QuickLinks';
import MobileBottomNav from './MobileBottomNav';
import MobileCameraFAB from './MobileCameraFAB';

// Navigation structure with sections
// `tab_id` (when present) maps to canonical access-control tab IDs defined in
// `backend/shared/access_catalog.py` (TAB_CATALOG) AND surfaced by the
// AccessOverridesPanel. Items without a tab_id mapping below are always visible.
const PATH_TO_TAB = {
  // AI
  '/ai-hub': 'command_center',
  '/memory': 'memory',
  '/all-conversations': 'all_conversations',
  '/ai-command': 'ai_command',
  '/cfo-buck': 'cfo_buck',
  '/ai-buck': 'ai_buck',
  '/reconcile-buck': 'reconcile_buck',
  // Money / Banking
  '/dashboard': 'dashboard',
  '/banking': 'banking',
  '/statement-importer': 'statement_importer',
  '/loan-qualification': 'loan_qualification',
  // Accounting
  '/invoices': 'invoices',
  '/bills': 'bills',
  '/contacts?type=customer': 'customers',
  '/contacts?type=vendor': 'vendors',
  '/contacts': 'contacts',
  '/messages': 'contacts',
  '/accounts': 'accounts',
  '/journal-entries': 'journal_entries',
  '/recurring-entries': 'recurring_entries',
  '/ledger': 'ledger',
  '/tax-mapper': 'tax_mapper',
  '/reports': 'reports',
  '/audit': 'audit',
  // Library
  '/documents': 'documents',
  '/signatures': 'signatures',
  '/client-replies': 'client_replies',
  '/inbox': 'client_replies',
  // Operations
  '/tasks': 'tasks',
  '/time-tracking': 'time_tracking',
  // Learn
  '/university': 'university',
  '/guided-tours': 'guided_tours',
  '/achievements': 'achievements',
  '/leaderboard': 'leaderboard',
  // Admin
  '/admin': 'admin_panel',
  '/billing': 'billing',
  '/integrations': 'integrations',
  '/settings': 'settings',
  '/switch-from-quickbooks': 'switch_from_quickbooks',
};

/** Returns true when the user has explicit hide for this tab. */
function isTabHidden(path, tabOverrides) {
  if (!tabOverrides) return false;
  // Strip query string for lookup
  const cleanPath = (path || '').split('?')[0];
  const tab = PATH_TO_TAB[cleanPath];
  if (!tab) return false;
  return tabOverrides[tab] === false;
}

// Simplified navigation (Jul 2026 UX overhaul): 7 core destinations grouped
// into 4 always-open sections + everything power-user grade collapsed under
// "Advanced". Every legacy route still works — nothing was removed, only
// reorganized so new users aren't hit with 35 menu items.
const navSections = [
  {
    title: 'Main',
    items: [
      { path: '/dashboard', label: 'Home', icon: Home, tooltip: 'Your financial overview — cash, income, expenses, and what needs attention.' },
      { path: '/ai-hub', label: 'AI Assistant', icon: Sparkles, tooltip: 'Chat with AI — ask about your finances OR how to use CountBucks.' },
      { path: '/documents', label: 'Documents', icon: FileText, tooltip: 'All your files in one place. Drop invoices, receipts, statements — AI Buck files them for you.' },
      { path: '/inbox', label: 'Client Inbox', icon: Inbox, badge: 'client_replies', tooltip: 'Replies from your clients — email + portal messages in one place.' },
    ]
  },
  {
    title: 'Money',
    items: [
      { path: '/banking', label: 'Banking', icon: Landmark, tooltip: 'Connected bank accounts and transactions.' },
      { path: '/invoices', label: 'Invoices', icon: FileText, tooltip: 'Bill your customers and get paid online.' },
      { path: '/bills', label: 'Bills', icon: Receipt, tooltip: 'Track what you owe vendors.' },
      { path: '/reports', label: 'Reports', icon: BarChart3, tooltip: 'P&L, balance sheet, and more.' },
    ]
  },
  {
    title: 'People',
    items: [
      { path: '/contacts?type=customer', label: 'Customers', icon: Users },
      { path: '/contacts?type=vendor', label: 'Vendors', icon: Users },
    ]
  },
  {
    title: 'Productivity',
    items: [
      { path: '/tasks', label: 'Tasks', icon: ClipboardList, tooltip: 'Manage and assign tasks across your team' },
      { path: '/time-tracking', label: 'Time Tracking', icon: Timer, tooltip: 'Track billable and non-billable hours' },
    ]
  },
  {
    title: 'Advanced',
    items: [
      { path: '/ai-command', label: 'Prompt Launchpad', icon: Rocket, tooltip: 'Browse 64+ AI prompts by category' },
      { path: '/cfo-buck', label: 'CFO Buck', icon: BrainCircuit, tooltip: 'AI-powered financial insights and advisory' },
      { path: '/memory', label: 'AI Memory', icon: Brain, tooltip: 'Pin goals, company facts, preferences — every chat remembers them.' },
      { path: '/all-conversations', label: 'All Conversations', icon: MessageSquare, tooltip: 'Every chat & saved plan across CountBucks' },
      { path: '/ai-buck', label: 'Auto-Categorizer', icon: Sparkles, badge: true, tooltip: 'AI auto-sorts your transactions into the right accounts' },
      { path: '/reconcile-buck', label: 'Smart Reconcile', icon: Zap, tooltip: 'Statement matching and auto-reconciliation' },
      { path: '/invoice-ocr', label: 'Document Scanner', icon: ScanLine, tooltip: 'AI Buck reads any document you drop and lights up Smart Actions.' },
      { path: '/statement-importer', label: 'Batch Statement Importer', icon: FileText, tooltip: 'Multi-page batch mode for bank statements and tax returns' },
      { path: '/loan-qualification', label: 'Loan Qualification', icon: BarChart3, tooltip: 'DSCR analysis and cash flow counseling' },
      { path: '/signatures', label: 'Signatures', icon: FileSignature, tooltip: 'Send PDFs for legally-binding electronic signatures.' },
      { path: '/accounts', label: 'Chart of Accounts', icon: BookOpen },
      { path: '/tax-mapper', label: 'Tax Mapper', icon: MapIcon, tooltip: 'Map your chart of accounts to tax form lines' },
      { path: '/journal-entries', label: 'Journal Entries', icon: FileText },
      { path: '/recurring-entries', label: 'Recurring Entries', icon: Repeat },
      { path: '/ledger', label: 'General Ledger', icon: BookMarked },
      { path: '/audit', label: 'Audit Log', icon: History },
      { path: '/switch-from-quickbooks', label: 'Switch from QuickBooks', icon: ArrowRightLeft },
      { path: '/university', label: 'University', icon: GraduationCap, tooltip: 'Business & accounting courses' },
      { path: '/guided-tours', label: 'Guided Tours', icon: MapIcon, tooltip: 'Interactive step-by-step walkthroughs for every feature' },
      { path: '/achievements', label: 'Achievements', icon: TrophyIcon },
      { path: '/leaderboard', label: 'Leaderboard', icon: MedalIcon },
    ]
  },
  {
    title: 'Admin',
    adminOnly: true,
    items: [
      { path: '/admin', label: 'Admin Panel', icon: Shield, tooltip: 'Manage team members and roles' },
      { path: '/settings', label: 'Settings', icon: Settings },
    ]
  }
];

// Sections expanded by default — keep the sidebar calm and uncluttered: only
// "Main" (Home, AI Assistant, Documents, Client Inbox) is open on a fresh load.
// Everything else starts collapsed; the active route's section auto-expands on
// navigation so context is never lost (e.g. opening Banking expands "Money").
const DEFAULT_OPEN_SECTIONS = new Set(['Main']);

// Breadcrumb configuration
const breadcrumbConfig = {
  '/dashboard': { label: 'Dashboard', icon: LayoutDashboard },
  '/all-conversations': { label: 'All Conversations', icon: MessageSquare },
  '/memory': { label: 'AI Memory', icon: Brain },
  '/switch-from-quickbooks': { label: 'Switch to CountBucks', icon: ArrowRightLeft },
  '/loan-qualification': { label: 'Loan Qualification', icon: BarChart3 },
  '/banking': { label: 'Banking', icon: Landmark },
  '/statement-importer': { label: 'Batch Statement Importer', icon: FileText },
  '/invoice-ocr': { label: 'Document Scanner', icon: ScanLine },
  '/inbox': { label: 'Client Inbox', icon: Inbox },
  '/tax-mapper': { label: 'Tax Mapper', icon: MapIcon },
  '/ai-buck': { label: 'Auto-Categorizer', icon: Sparkles },
  '/reconcile-buck': { label: 'Smart Reconcile', icon: Zap },
  '/accounts': { label: 'Chart of Accounts', icon: BookOpen },
  '/journal-entries': { label: 'Journal Entries', icon: FileText },
  '/ledger': { label: 'General Ledger', icon: BookMarked },
  '/reports': { label: 'Reports', icon: BarChart3 },
  '/audit': { label: 'Audit Log', icon: History },
  '/cfo-buck': { label: 'CFO Buck', icon: BrainCircuit },
  '/chat-history': { label: 'Chat History', icon: MessageCircle },
  '/contacts': { label: 'Contacts', icon: Users },
  '/messages': { label: 'Client Inbox', icon: Inbox },
  '/invoices': { label: 'Invoices', icon: FileText },
  '/checklists': { label: 'Document Checklists', icon: ClipboardList },
  '/bills': { label: 'Bills', icon: Receipt },
  '/batch-import': { label: 'Batch Import', icon: Upload },
  '/settings': { label: 'Settings', icon: Settings },
  '/admin': { label: 'Admin', icon: Shield },
  '/tasks': { label: 'Task Manager', icon: ClipboardList },
  '/time-tracking': { label: 'Time Tracking', icon: Timer },
  '/recurring-entries': { label: 'Recurring Entries', icon: Repeat },
  '/document-inbox': { label: 'Document Inbox', icon: Inbox },
  '/university': { label: 'University', icon: GraduationCap },
  '/guided-tours': { label: 'Guided Tours', icon: MapIcon },
  '/achievements': { label: 'Achievements', icon: TrophyIcon },
  '/leaderboard': { label: 'Leaderboard', icon: MedalIcon },
  '/ai-command': { label: 'Prompt Launchpad', icon: Rocket },
  '/hubs/legal': { label: 'Legal Hub', icon: Scale },
  '/hubs/hr': { label: 'HR Hub', icon: Users },
  '/hubs/marketing': { label: 'Marketing Hub', icon: Megaphone },
  '/hubs/knowledge': { label: 'Knowledge Hub', icon: KnowledgeIcon },
  '/hubs/crm': { label: 'CRM Hub', icon: Handshake },
  '/hubs/finance': { label: 'Finance Hub', icon: TrendingUp },
  '/hubs/operations': { label: 'Operations Hub', icon: Workflow },
  '/hubs/productivity': { label: 'Productivity Hub', icon: Zap },
  '/ai-hub': { label: 'AI Studio', icon: Sparkles },
  '/documents': { label: 'Documents Hub', icon: FileText },
  '/support': { label: 'Help & Support', icon: HelpCircle },
  '/signatures': { label: 'Signatures', icon: FileSignature },
  '/folder-templates': { label: 'Folder Templates', icon: FolderTree },
};

// Breadcrumb component
function Breadcrumbs() {
  const location = useLocation();
  const pathParts = location.pathname.split('/').filter(Boolean);
  
  const breadcrumbs = useMemo(() => {
    const crumbs = [{ path: '/dashboard', label: 'Home', icon: Home }];
    
    let currentPath = '';
    for (const part of pathParts) {
      currentPath += `/${part}`;
      // Skip /dashboard — it's already added as the "Home" crumb above,
      // so re-adding it via breadcrumbConfig['/dashboard'] would create a
      // duplicate React key (both crumbs share path='/dashboard').
      if (currentPath === '/dashboard') continue;
      const config = breadcrumbConfig[currentPath];
      if (config) {
        crumbs.push({ path: currentPath, ...config });
      } else if (part !== 'dashboard') {
        // Handle dynamic paths like /journal-entries/:id
        crumbs.push({ path: currentPath, label: part });
      }
    }
    
    return crumbs;
  }, [pathParts]);
  
  if (breadcrumbs.length <= 1) return null;
  
  return (
    <nav className="flex items-center gap-1 text-sm" aria-label="Breadcrumb">
      {breadcrumbs.map((crumb, i) => {
        const isLast = i === breadcrumbs.length - 1;
        const Icon = crumb.icon;
        
        return (
          <div key={crumb.path} className="flex items-center gap-1">
            {i > 0 && (
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            )}
            {isLast ? (
              <span className="flex items-center gap-1.5 text-foreground font-medium">
                {Icon && <Icon className="w-4 h-4" />}
                {crumb.label}
              </span>
            ) : (
              <Link 
                to={crumb.path}
                className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors"
              >
                {Icon && <Icon className="w-4 h-4" />}
                {crumb.label}
              </Link>
            )}
          </div>
        );
      })}
    </nav>
  );
}

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  // OwnerAdmin lives in a separate auth realm (cookie-based session at
  // /api/owner-admin/*) — we do a single best-effort fetch to know if
  // *this browser* is also logged in as an admin so we can surface an
  // Admin Panel shortcut in the avatar dropdown. 401 = not admin (silent).
  const [ownerAdmin, setOwnerAdmin] = useState(null);
  // Booking-credit balance — used to power the "Book a Call or Meeting" badge
  // in the top bar (Platinum/DFY subscribers only). Silent on failure.
  const [bookingCredits, setBookingCredits] = useState(null);
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/owner-admin/auth/me`, { credentials: 'include' });
        if (res.ok && !cancelled) setOwnerAdmin(await res.json());
      } catch { /* silent — non-admins get 401 here */ }
    })();
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/appointments/my-credits`, { credentials: 'include' });
        if (res.ok && !cancelled) setBookingCredits(await res.json());
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [user]);
  const { theme, setTheme, themes, getThemeName } = useTheme();
  const { workspaces, currentWorkspace, selectWorkspace, createWorkspace, loading: wsLoading, userRole, refreshWorkspaces } = useWorkspace();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebarCompact') === 'true'; } catch { return false; }
  });
  const [newWsDialogOpen, setNewWsDialogOpen] = useState(false);
  // Prefill from the pre-paywall stash — if the user typed their business
  // name before being sent to Stripe (402 PLAN_REQUIRED redirect), restore
  // it so they never have to type it twice.
  const [newWsName, setNewWsName] = useState(() => {
    try { return localStorage.getItem('pending_business_name') || ''; } catch { return ''; }
  });
  const [newWsCurrency, setNewWsCurrency] = useState('USD');
  const [newWsType, setNewWsType] = useState('business');
  const [newWsIndustry, setNewWsIndustry] = useState('general');
  // Every new signup silently defaults to the Small Business template — we
  // no longer show the picker on the trial-signup screen (minimum friction).
  // Existing multi-workspace users still choose in the dialog further below.
  const [newWsTemplateKey, setNewWsTemplateKey] = useState('small_business');
  const [industryTemplates, setIndustryTemplates] = useState([]);
  const [aiBuckCount, setAiBuckCount] = useState(0);
  const [clientRepliesUnread, setClientRepliesUnread] = useState(0);
  const [portalMessagesUnread, setPortalMessagesUnread] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  // Sections in this set are EXPANDED. Default: only 'Home' is expanded.
  const [openSections, setOpenSections] = useState(() => new Set(DEFAULT_OPEN_SECTIONS));
  const [tabOverrides, setTabOverrides] = useState({});

  // ── Favorites (Quick Access) ───────────────────────────────────────────
  // Pin up to 6 sidebar items for one-click access. Per-user, persisted in
  // localStorage. The Favorites section is virtual — built on render from
  // the saved paths matched against navSections.
  const FAV_MAX = 6;
  const favStorageKey = `cb_sidebar_favorites_${user?.user_id || 'anon'}`;
  const [favorites, setFavorites] = useState(() => {
    try {
      const raw = localStorage.getItem(favStorageKey);
      return raw ? JSON.parse(raw) : [];
    } catch (_e) { return []; }
  });
  useEffect(() => {
    // When user changes (sign in / switch), reload favorites for that user
    try {
      const raw = localStorage.getItem(favStorageKey);
      setFavorites(raw ? JSON.parse(raw) : []);
    } catch (_e) { setFavorites([]); }
  }, [favStorageKey]);

  const isFavorite = (path) => favorites.includes(path);
  const toggleFavorite = (path) => {
    setFavorites((prev) => {
      let next;
      if (prev.includes(path)) {
        next = prev.filter((p) => p !== path);
        track('favorite.unpinned', { path }, currentWorkspace?.workspace_id);
      } else {
        if (prev.length >= FAV_MAX) {
          toast.info(`You can pin up to ${FAV_MAX} favorites. Remove one first.`);
          return prev;
        }
        next = [...prev, path];
        track('favorite.pinned', { path, source: 'manual' }, currentWorkspace?.workspace_id);
      }
      try { localStorage.setItem(favStorageKey, JSON.stringify(next)); } catch (_e) { /* silent */ }
      return next;
    });
  };

  // Pin from external triggers (auto-suggest toast) without the unique-check
  // toast spam — internal helper used by useFavoritesAutoSuggest.
  const pinFavorite = (path) => {
    setFavorites((prev) => {
      if (prev.includes(path)) return prev;
      const next = prev.length >= FAV_MAX ? [...prev.slice(1), path] : [...prev, path];
      try { localStorage.setItem(favStorageKey, JSON.stringify(next)); } catch (_e) { /* silent */ }
      toast.success('Pinned to Quick Access');
      track('favorite.suggested_accepted', { path }, currentWorkspace?.workspace_id);
      return next;
    });
  };

  // Smart auto-suggest pop-up — DISABLED per user request (Feb 2026).
  // Was: a sonner toast prompting "Pin {label} to Quick Access?" after 5+
  // visits in 7 days. Users can still pin manually via the ★ icon on each
  // sidebar item. Re-enable by uncommenting the call below.
  // useFavoritesAutoSuggest({
  //   userId: user?.user_id,
  //   pathname: location.pathname,
  //   search: location.search,
  //   navSections,
  //   isFavorite,
  //   onPin: pinFavorite,
  // });

  // Build the virtual Favorites section from the saved paths.
  const favoriteSection = useMemo(() => {
    if (favorites.length === 0) return null;
    const lookup = new Map();
    for (const s of navSections) {
      for (const item of s.items) lookup.set(item.path, item);
    }
    const items = favorites.map((p) => lookup.get(p)).filter(Boolean);
    if (items.length === 0) return null;
    return { title: 'Quick Access', items, isFavoritesGroup: true };
  }, [favorites]);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch AI Buck pending suggestions count
  useEffect(() => {
    const fetchAiBuckCount = async () => {
      if (!currentWorkspace?.workspace_id) return;
      
      try {
        const response = await apiFetch(
          `${API_URL}/api/ai-buck/suggestions?workspace_id=${currentWorkspace.workspace_id}&status=pending&limit=1`
        );
        if (response.ok) {
          const data = await response.json();
          setAiBuckCount(data.total || 0);
        }
      } catch (error) {
        console.error('Failed to fetch AI Buck count:', error);
      }
    };

    fetchAiBuckCount();
    const interval = setInterval(fetchAiBuckCount, 60000);
    return () => clearInterval(interval);
  }, [currentWorkspace?.workspace_id, API_URL]);

  // Fetch client replies unread count (Resend inbound inbox)
  useEffect(() => {
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const params = new URLSearchParams({ unread_only: 'true', limit: '1' });
        if (currentWorkspace?.workspace_id) {
          params.set('workspace_id', currentWorkspace.workspace_id);
        }
        const r = await apiFetch(`${API_URL}/api/inbound/messages?${params.toString()}`);
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setClientRepliesUnread(data.unread_count || 0);
      } catch {
        /* sidebar widget — fail silently */
      }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [currentWorkspace?.workspace_id, API_URL]);

  // Fetch portal messages unread count (sum unread across threads).
  // This is the firm-side inbox at /messages — messages your CLIENTS sent
  // from their Client Portal. Separate from /client-replies (email replies).
  useEffect(() => {
    let cancelled = false;
    const fetchPortalUnread = async () => {
      if (!currentWorkspace?.workspace_id) return;
      try {
        const r = await apiFetch(
          `${API_URL}/api/client-portal/workspace-messages?workspace_id=${currentWorkspace.workspace_id}`,
        );
        if (!r.ok) return;
        const data = await r.json();
        const total = (data.threads || []).reduce((sum, t) => sum + (t.unread || 0), 0);
        if (!cancelled) setPortalMessagesUnread(total);
      } catch {
        /* sidebar widget — fail silently */
      }
    };
    fetchPortalUnread();
    const interval = setInterval(fetchPortalUnread, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [currentWorkspace?.workspace_id, API_URL]);

  // Fetch industry templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await apiFetch(`${API_URL}/api/coa-templates`);
        if (res.ok) {
          const data = await res.json();
          setIndustryTemplates(data.templates || []);
        }
      } catch (e) {
        console.error('Failed to fetch COA templates:', e);
      }
    };
    fetchTemplates();
  }, [API_URL]);

  // Fetch this user's tab overrides for the current workspace so we can hide
  // sidebar items the admin has explicitly disabled for them.
  useEffect(() => {
    const fetchOverrides = async () => {
      if (!currentWorkspace?.workspace_id || !user?.user_id) {
        setTabOverrides({});
        return;
      }
      try {
        const r = await apiFetch(
          `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/members/${user.user_id}/tab-overrides`,
        );
        if (r.ok) {
          const data = await r.json();
          setTabOverrides(data.tab_overrides || {});
        } else {
          setTabOverrides({});
        }
      } catch {
        setTabOverrides({});
      }
    };
    fetchOverrides();
  }, [currentWorkspace?.workspace_id, user?.user_id, API_URL]);

  // Keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const toggleSection = (title) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });
  };

  // Auto-expand the section that contains the active route on navigation.
  // Per user preference, we do NOT persist expansion state across reloads —
  // a fresh page load resets to DEFAULT_OPEN_SECTIONS, but navigating during
  // a session expands siblings so the user can see context.
  useEffect(() => {
    const pathname = location.pathname;
    const search = location.search;
    for (const section of navSections) {
      const match = section.items.some(item => {
        const [itemPath, itemQuery] = item.path.split('?');
        if (itemQuery) {
          return pathname === itemPath && new URLSearchParams(itemQuery).toString() === new URLSearchParams(search).toString();
        }
        return pathname === itemPath || (itemPath !== '/dashboard' && pathname.startsWith(itemPath + '/'));
      });
      if (match) {
        setOpenSections(prev => {
          if (prev.has(section.title)) return prev;
          const next = new Set(prev);
          next.add(section.title);
          return next;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  const handleCreateWorkspace = async () => {
    if (!newWsName.trim()) return;
    const isFirstWorkspace = workspaces.length === 0;
    // Stash the name BEFORE attempting creation — if the backend paywall
    // 402s and redirects to Stripe checkout, the /welcome page auto-creates
    // the workspace with this name after payment succeeds.
    try { localStorage.setItem('pending_business_name', newWsName.trim()); } catch { /* ignore */ }
    // Defer any staged Stripe auto-checkout / hard-gate until the setup
    // wizard is done — otherwise it races the /setup-wizard navigation.
    deferGatingForSetup();
    const ws = await createWorkspace(newWsName.trim(), newWsCurrency, newWsType, newWsIndustry, newWsTemplateKey);
    if (ws) {
      try { localStorage.removeItem('pending_business_name'); } catch { /* ignore */ }
      if (isFirstWorkspace) {
        trackFunnel('workspace.created', { extra: { template: newWsTemplateKey, industry: newWsIndustry } });
      }
      setNewWsDialogOpen(false);
      setNewWsName('');
      setNewWsCurrency('USD');
      setNewWsType('business');
      setNewWsIndustry('general');
      setNewWsTemplateKey(null);
      // Redirect new workspace owners to the setup wizard so they get
      // industry-tailored folders out of the box.
      navigate(`/setup-wizard/${ws.workspace_id}`);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Show workspace creation prompt if no workspaces
  if (!wsLoading && workspaces.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6" data-tour-block>
        <div className="w-full max-w-md">
          {/* Brand mark */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center gap-2">
              <CBLogo size="xl" />
              <span className="text-xl font-bold tracking-tight">CountBucks</span>
            </div>
          </div>

          <div className="bg-card rounded-2xl border border-border shadow-sm p-8 space-y-6">
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold tracking-tight">Name your business</h2>
              <p className="text-muted-foreground text-sm">
                One quick step and you&apos;re in. You can rename or add more later.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ws-name" className="sr-only">Business name</Label>
              <Input
                id="ws-name"
                placeholder="e.g. Acme Consulting LLC"
                value={newWsName}
                onChange={(e) => setNewWsName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newWsName.trim()) handleCreateWorkspace();
                }}
                autoFocus
                className="h-12 text-base text-center"
                data-testid="workspace-name-input"
              />
            </div>

            <Button
              onClick={handleCreateWorkspace}
              disabled={!newWsName.trim()}
              className="w-full h-12 text-base font-semibold"
              data-testid="create-workspace-btn"
            >
              Continue
              <Plus className="w-4 h-4 ml-2" />
            </Button>

            <p className="text-center text-xs text-muted-foreground">
              We&apos;ll set up your books in USD with a starter chart of accounts. Change anything later in Settings.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <ImpersonationBanner />
      <AdminViewBanner />
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed top-0 left-0 z-50 h-full bg-card border-r border-border transition-all duration-300",
        "lg:translate-x-0",
        sidebarCollapsed ? "w-16" : "w-64",
        sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-border">
            <Link to="/dashboard" className="flex items-center gap-2.5">
              {/* Branded CB mark — orange C + emerald B, scalable SVG */}
              <CBLogo size="lg" className="flex-shrink-0" />
              {!sidebarCollapsed && (
                <span className="font-bold text-lg bg-gradient-to-r from-brand-600 to-brand-400 dark:from-brand-400 dark:to-brand-300 bg-clip-text text-transparent">
                  CountBucks
                </span>
              )}
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Workspace switcher lives in the top header (WorkspaceContextPill).
            * The duplicated sidebar version was removed Feb 29 2026 — having two
            * dropdowns side-by-side caused user confusion ("which one do I use?")
            * and the new pill is always visible across both collapsed/expanded
            * sidebar states AND on mobile, so the sidebar version is redundant. */}

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-2">
            {[
              ...(favoriteSection ? [favoriteSection] : []),
              ...navSections,
            ]
              .filter(section => !section.adminOnly || ['owner', 'admin'].includes(userRole))
              .map((section) => {
                const isQuickAccess = !!section.isFavoritesGroup;
                // Quick Access is always expanded; other sections honor user toggle
                const isOpen = isQuickAccess ? true : openSections.has(section.title);
                // Filter out tabs explicitly hidden by admin-set tab_overrides
                const visibleItems = section.items.filter(item => !isTabHidden(item.path, tabOverrides));
                if (visibleItems.length === 0) return null;
                
                return (
                  <div key={section.title} className="mb-1">
                    {/* Section header */}
                    {!sidebarCollapsed && (
                      isQuickAccess ? (
                        <div
                          className="w-full flex items-center justify-between px-4 py-2.5 mt-1 text-[11px] font-bold uppercase tracking-[0.12em] border-b border-border/40 text-amber-600 dark:text-amber-400"
                          data-testid="nav-section-quick-access"
                        >
                          <span className="flex items-center gap-1.5">
                            <Star className="w-3 h-3 fill-current" />
                            {section.title}
                            <span className="text-[10px] font-mono font-normal text-muted-foreground/50">
                              {visibleItems.length}
                            </span>
                          </span>
                        </div>
                      ) : (
                      <button
                        onClick={() => toggleSection(section.title)}
                        className="w-full flex items-center justify-between px-4 py-2.5 mt-1 text-[11px] font-bold text-foreground/70 uppercase tracking-[0.12em] hover:text-foreground transition-colors border-b border-border/40"
                        data-testid={`nav-section-${section.title.toLowerCase().replace(/\s+/g, '-')}`}
                        aria-expanded={isOpen}
                      >
                        <span className="flex items-center gap-2">
                          {section.title}
                          <span className="text-[10px] font-mono font-normal text-muted-foreground/50">
                            {visibleItems.length}
                          </span>
                        </span>
                        <ChevronDown className={cn(
                          "w-3 h-3 transition-transform",
                          !isOpen && "-rotate-90"
                        )} />
                      </button>
                      )
                    )}
                    
                    {/* Section items */}
                    {(isOpen || sidebarCollapsed) && (
                      <div className={cn("space-y-0.5", sidebarCollapsed ? "px-2" : "px-2")}>
                        {visibleItems.map((item) => {
                          const [itemPath, itemQuery] = item.path.split('?');
                          const itemParams = new URLSearchParams(itemQuery || '');
                          const currentParams = new URLSearchParams(location.search);
                          // If item has query params (e.g. type=customer), require exact match
                          let isActive;
                          if (itemQuery) {
                            isActive = location.pathname === itemPath &&
                              Array.from(itemParams.entries()).every(([k, v]) => currentParams.get(k) === v);
                          } else {
                            // For items without query params, exact path match OR descendant route
                            // BUT skip if any sibling has a query-param version of same path (avoids double-highlight)
                            const hasQuerySibling = visibleItems.some(s => s !== item && s.path.split('?')[0] === itemPath && s.path.includes('?'));
                            if (hasQuerySibling) {
                              isActive = location.pathname === itemPath && !location.search;
                            } else {
                              isActive = location.pathname === itemPath || 
                                (itemPath !== '/dashboard' && location.pathname.startsWith(itemPath + '/'));
                            }
                          }
                          
                          const fav = isFavorite(item.path);
                          const NavItem = (
                            <div key={item.path + item.label} className="relative group/nav">
                              <Link
                                to={item.path}
                                onClick={() => setSidebarOpen(false)}
                                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                                className={cn(
                                  "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all",
                                  sidebarCollapsed && "justify-center px-2",
                                  isActive
                                    ? "bg-gradient-to-r from-emerald-500/10 to-blue-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20"
                                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                                )}
                              >
                                <item.icon className={cn(
                                  "w-5 h-5 flex-shrink-0",
                                  isActive && "text-emerald-500"
                                )} />
                                {!sidebarCollapsed && (
                                  <>
                                    <span className="flex-1 truncate">{item.label}</span>
                                    {item.badge === true && aiBuckCount > 0 && (
                                      <Badge
                                        className={cn(
                                          "min-w-[20px] h-5 px-1.5 text-[10px] font-semibold",
                                          isActive
                                            ? "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400"
                                            : "bg-amber-500 text-white"
                                        )}
                                      >
                                        {aiBuckCount > 99 ? '99+' : aiBuckCount}
                                      </Badge>
                                    )}
                                    {item.badge === 'client_replies' && clientRepliesUnread > 0 && (
                                      <Badge
                                        data-testid="nav-client-replies-unread-badge"
                                        className={cn(
                                          "min-w-[20px] h-5 px-1.5 text-[10px] font-semibold",
                                          isActive
                                            ? "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400"
                                            : "bg-emerald-500 text-white"
                                        )}
                                      >
                                        {clientRepliesUnread > 99 ? '99+' : clientRepliesUnread}
                                      </Badge>
                                    )}
                                    {item.badge === 'portal_messages' && portalMessagesUnread > 0 && (
                                      <Badge
                                        data-testid="nav-portal-messages-unread-badge"
                                        className={cn(
                                          "min-w-[20px] h-5 px-1.5 text-[10px] font-semibold",
                                          isActive
                                            ? "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400"
                                            : "bg-emerald-500 text-white"
                                        )}
                                      >
                                        {portalMessagesUnread > 99 ? '99+' : portalMessagesUnread}
                                      </Badge>
                                    )}
                                  </>
                                )}
                              </Link>
                              {/* Star toggle — visible on hover OR when already favorited; never in Quick Access (always favorited) and never on collapsed rail */}
                              {!sidebarCollapsed && !isQuickAccess && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(item.path); }}
                                  title={fav ? 'Remove from Quick Access' : 'Pin to Quick Access'}
                                  aria-label={fav ? 'Unpin' : 'Pin'}
                                  data-testid={`nav-fav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                                  className={cn(
                                    'absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-opacity',
                                    fav
                                      ? 'opacity-100 text-amber-500 hover:bg-amber-500/10'
                                      : 'opacity-0 group-hover/nav:opacity-100 text-muted-foreground hover:text-amber-500 hover:bg-amber-500/10',
                                  )}
                                >
                                  {fav
                                    ? <Star className="w-3.5 h-3.5 fill-current" />
                                    : <Star className="w-3.5 h-3.5" />}
                                </button>
                              )}
                              {!sidebarCollapsed && isQuickAccess && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(item.path); }}
                                  title="Remove from Quick Access"
                                  aria-label="Unpin"
                                  data-testid={`nav-unpin-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-amber-500 hover:text-red-500 opacity-0 group-hover/nav:opacity-100 transition-opacity"
                                >
                                  <StarOff className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          );
                          
                          if (sidebarCollapsed && (item.tooltip || item.label)) {
                            return (
                              <TooltipProvider key={item.path}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    {NavItem}
                                  </TooltipTrigger>
                                  <TooltipContent side="right">
                                    <p>{item.label}</p>
                                    {item.badge === true && aiBuckCount > 0 && (
                                      <p className="text-xs text-muted-foreground">{aiBuckCount} pending</p>
                                    )}
                                    {item.badge === 'client_replies' && clientRepliesUnread > 0 && (
                                      <p className="text-xs text-muted-foreground">{clientRepliesUnread} unread</p>
                                    )}
                                    {item.badge === 'portal_messages' && portalMessagesUnread > 0 && (
                                      <p className="text-xs text-muted-foreground">{portalMessagesUnread} unread</p>
                                    )}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            );
                          }
                          
                          return NavItem;
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
          </nav>

          {/* Compact Mode toggle (desktop) */}
          <div className="hidden lg:flex items-center justify-center py-2 border-t border-border flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                const next = !sidebarCollapsed;
                setSidebarCollapsed(next);
                try { localStorage.setItem('sidebarCompact', String(next)); } catch {}
              }}
              className="w-full mx-2"
              data-testid="sidebar-collapse-btn"
            >
              <ChevronRight className={cn(
                "w-4 h-4 transition-transform",
                sidebarCollapsed ? "rotate-0" : "rotate-180"
              )} />
              {!sidebarCollapsed && <span className="ml-2 text-xs">Compact Mode</span>}
            </Button>
          </div>

          {/* User Section */}
          <div className={cn(
            "border-t border-border flex-shrink-0",
            sidebarCollapsed ? "p-2" : "p-3"
          )}>
            {sidebarCollapsed ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Avatar className="w-10 h-10 mx-auto cursor-pointer">
                      <AvatarImage src={user?.picture} />
                      <AvatarFallback className="bg-gradient-to-br from-emerald-500 to-blue-500 text-white">
                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <p className="font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <div className="flex items-center gap-3">
                <Avatar className="w-10 h-10 flex-shrink-0">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-gradient-to-br from-emerald-500 to-blue-500 text-white">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={cn(
        "transition-all duration-300 pb-16 lg:pb-0",
        sidebarCollapsed ? "lg:pl-16" : "lg:pl-64"
      )}>
        {/* Top Header */}
        <header className="h-14 border-b border-border bg-background/95 backdrop-blur-sm sticky top-0 z-30 relative">
          <div className="flex items-center justify-between h-full px-4 lg:px-6">
            {/* Left side - Mobile menu + Workspace Pill + Breadcrumbs */}
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1 lg:flex-none">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden flex-shrink-0"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-5 h-5" />
              </Button>

              {/* Always-visible workspace context pill — shows colored initial
                * badge + name + chevron. Clicking opens the switcher with
                * active/recent ordering, color chips per workspace, and
                * deep links to New / Manage. ⌘W also toggles. Solves the
                * post-remodel "which workspace am I in?" complaint. */}
              <WorkspaceContextPill
                currentWorkspace={currentWorkspace}
                workspaces={workspaces}
                selectWorkspace={selectWorkspace}
                onCreateWorkspace={() => setNewWsDialogOpen(true)}
                onIconUpdated={refreshWorkspaces}
              />

              {/* Breadcrumbs - hidden on mobile */}
              <div className="hidden md:block">
                <Breadcrumbs />
              </div>
            </div>

            {/* Right side - Search, Theme, User */}
            <div className="flex items-center gap-2">
              {/* Global Search Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSearchOpen(true)}
                className="hidden sm:flex items-center gap-2 text-muted-foreground h-9 px-3"
                data-testid="global-search-btn"
              >
                <Search className="w-4 h-4" />
                <span className="text-sm">Search...</span>
                <kbd className="hidden lg:inline-flex items-center gap-1 px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">
                  <Command className="w-3 h-3" />K
                </kbd>
              </Button>
              
              {/* Book a Call or Meeting — top-center prominence for
                * revenue. Shows a "N min free" badge when signed-in user
                * is a Platinum/Done For You subscriber with unused
                * monthly booking credits, nudging them to use what they
                * already paid for. */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open('/book', '_blank')}
                      className="relative flex items-center gap-1.5 font-semibold border border-[#FF7816]/30 bg-[#FF7816]/8 text-[#B14A00] hover:bg-[#FF7816]/15 hover:text-[#B14A00] dark:text-[#FFB176] dark:bg-[#FF7816]/10 dark:border-[#FF7816]/40 dark:hover:bg-[#FF7816]/20 px-3 rounded-full"
                      data-testid="topbar-book-call-btn"
                    >
                      <PhoneCall className="w-4 h-4" />
                      <span className="text-sm hidden sm:inline">Book a Call or Meeting</span>
                      <span className="text-sm sm:hidden">Book</span>
                      {bookingCredits?.eligible && bookingCredits.total_minutes > 0 && (
                        <span
                          className="ml-1 inline-flex items-center gap-0.5 text-[10px] font-bold leading-none rounded-full bg-[#27B84A] text-white px-1.5 py-0.5 shadow-sm"
                          data-testid="topbar-credits-badge"
                        >
                          <Sparkles className="w-2.5 h-2.5" />
                          {bookingCredits.total_minutes}m free
                        </span>
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {bookingCredits?.eligible && bookingCredits.total_minutes > 0
                      ? `You have ${bookingCredits.total_minutes} free minutes on your ${bookingCredits.plan_id === 'done_for_you' ? 'Done For You' : 'Platinum'} plan — click to book a call or in-person meeting`
                      : 'Book a phone call or in-person meeting with our internal CPA Advisor ($150/hr, 15-min increments)'}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {/* CFO Buck top-bar launcher removed (Oct 2026) — redundant with
                * the AI Assistant. Users chat with AI from /ai-hub now. */}

              {/* In-app notification bell — pings users when async jobs
                  (OCR scans, tax-savings reports) finish so they can
                  navigate away during a long scan. */}
              <NotificationBell />

              {/* Help — dropdown with tour, shortcuts, docs, support */}
              <HelpMenu />

              {/* Theme Toggle */}
              {/* Theme Selector Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="theme-toggle"
                    aria-label="Change theme"
                    className="gap-1 sm:gap-2 px-2 sm:px-3"
                  >
                    {theme === 'dark' ? (
                      <Moon className="w-4 h-4" />
                    ) : theme === 'midnight' ? (
                      <Sparkles className="w-4 h-4" />
                    ) : theme === 'classic' ? (
                      <Monitor className="w-4 h-4" />
                    ) : (
                      <Sun className="w-4 h-4" />
                    )}
                    <span className="hidden lg:inline text-sm">{getThemeName(theme)}</span>
                    <ChevronDown className="w-3 h-3 opacity-50 hidden sm:inline" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wide">
                    Theme
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => setTheme('light')}
                    className={cn("cursor-pointer gap-2", theme === 'light' && "bg-muted")}
                  >
                    <Sun className="w-4 h-4" />
                    Light
                    {theme === 'light' && <Badge variant="secondary" className="ml-auto text-[10px]">Active</Badge>}
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => setTheme('dark')}
                    className={cn("cursor-pointer gap-2", theme === 'dark' && "bg-muted")}
                  >
                    <Moon className="w-4 h-4" />
                    Dark
                    {theme === 'dark' && <Badge variant="secondary" className="ml-auto text-[10px]">Active</Badge>}
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => setTheme('midnight')}
                    className={cn("cursor-pointer gap-2", theme === 'midnight' && "bg-muted")}
                  >
                    <Sparkles className="w-4 h-4" />
                    Midnight
                    {theme === 'midnight' && <Badge variant="secondary" className="ml-auto text-[10px]">Active</Badge>}
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => setTheme('classic')}
                    className={cn("cursor-pointer gap-2", theme === 'classic' && "bg-muted")}
                  >
                    <Monitor className="w-4 h-4" />
                    Classic
                    {theme === 'classic' && <Badge variant="secondary" className="ml-auto text-[10px]">Active</Badge>}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* AI Credits Pill — primary surface for "how much AI I have left"
                  with an Emergent-style dropdown breakdown (premium spend,
                  free-tier counter, storage, top-up CTA). */}
              <HeaderLimitPill />

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="user-menu" className="rounded-full">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={user?.picture} />
                      <AvatarFallback className="bg-gradient-to-br from-emerald-500 to-blue-500 text-white text-sm">
                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div>
                      <p className="font-medium">{user?.name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {/* Mobile-only shortcuts (hidden on md+ where they appear in the top bar) */}
                  <DropdownMenuItem
                    onClick={() => navigate('/ai-hub')}
                    className="cursor-pointer md:hidden"
                  >
                    <Brain className="w-4 h-4 mr-2 text-violet-500" />
                    AI Usage
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="md:hidden" />
                  <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer" data-testid="dropdown-settings">
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/settings?tab=billing')} className="cursor-pointer" data-testid="dropdown-buy-credits">
                    <Sparkles className="w-4 h-4 mr-2 text-emerald-600" />
                    Buy Credits
                  </DropdownMenuItem>
                  {ownerAdmin
                    && !user?.impersonation
                    && (ownerAdmin.email || '').toLowerCase() === (user?.email || '').toLowerCase()
                    && (
                    <DropdownMenuItem onClick={() => navigate('/owner-admin')} className="cursor-pointer" data-testid="dropdown-admin-panel">
                      <ShieldCheck className="w-4 h-4 mr-2 text-violet-500" />
                      Admin Panel
                      <Badge variant="outline" className="ml-auto text-[9px] bg-violet-500/10 text-violet-500 border-violet-500/30">
                        Owner
                      </Badge>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate('/support')} className="cursor-pointer" data-testid="dropdown-support">
                    <HelpCircle className="w-4 h-4 mr-2" />
                    Help & Support
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="cursor-pointer text-red-600 dark:text-red-400">
                    <LogOut className="w-4 h-4 mr-2" />
                    Log Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content — AI Studio uses full-height layout, no outer padding */}
        <main
          className={cn(
            location.pathname.startsWith('/ai-hub') ? 'p-0' : 'p-3 sm:p-4 lg:p-6',
          )}
        >
          {/* QuickLinks — section-aware shortcut strip. Hidden on AI Studio
            * routes (they have their own hero layout) and on routes that don't
            * map to any section. */}
          {!location.pathname.startsWith('/ai-hub') && (
            <QuickLinks />
          )}
          {/* First-run product tour CTA — banner self-gates by route and
              dismissal state, so this is safe to mount globally. */}
          <ProductTourBanner />
          {children}
        </main>
        
        {/* Tour Launcher */}
        <TourLauncher />
      </div>

      {/* Mobile bottom navigation — hidden on lg+ */}
      <MobileBottomNav
        onOpenSidebar={() => setSidebarOpen(true)}
        unreadReplies={clientRepliesUnread}
      />

      {/* Mobile camera FAB — photo → bill OCR flow */}
      <MobileCameraFAB />

      {/* New Workspace Dialog */}
      <Dialog open={newWsDialogOpen} onOpenChange={setNewWsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Workspace</DialogTitle>
            <DialogDescription>
              Create a new workspace with its own chart of accounts and financial data.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Workspace Type</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setNewWsType('business')}
                  className={cn(
                    "flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all",
                    newWsType === 'business'
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-muted-foreground/30"
                  )}
                  data-testid="dialog-workspace-type-business"
                >
                  <Building2 className={cn("w-5 h-5", newWsType === 'business' ? "text-primary" : "text-muted-foreground")} />
                  <span className={cn("text-sm font-medium", newWsType === 'business' ? "text-foreground" : "text-muted-foreground")}>Business</span>
                </button>
                <button
                  onClick={() => setNewWsType('personal')}
                  className={cn(
                    "flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all",
                    newWsType === 'personal'
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-muted-foreground/30"
                  )}
                  data-testid="dialog-workspace-type-personal"
                >
                  <Home className={cn("w-5 h-5", newWsType === 'personal' ? "text-primary" : "text-muted-foreground")} />
                  <span className={cn("text-sm font-medium", newWsType === 'personal' ? "text-foreground" : "text-muted-foreground")}>Personal</span>
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="dialog-ws-name">Workspace Name</Label>
              <Input
                id="dialog-ws-name"
                placeholder="My Company"
                value={newWsName}
                onChange={(e) => setNewWsName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dialog-ws-currency">Base Currency</Label>
              <Select value={newWsCurrency} onValueChange={setNewWsCurrency}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD - US Dollar</SelectItem>
                  <SelectItem value="EUR">EUR - Euro</SelectItem>
                  <SelectItem value="GBP">GBP - British Pound</SelectItem>
                  <SelectItem value="CAD">CAD - Canadian Dollar</SelectItem>
                  <SelectItem value="AUD">AUD - Australian Dollar</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {industryTemplates.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="dialog-ws-industry">Industry Template</Label>
                <Select value={newWsIndustry} onValueChange={setNewWsIndustry}>
                  <SelectTrigger data-testid="dialog-workspace-industry-select">
                    <SelectValue placeholder="Select industry..." />
                  </SelectTrigger>
                  <SelectContent>
                    {industryTemplates.map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        <div className="flex items-center justify-between w-full gap-2">
                          <span>{t.name}</span>
                          <span className="text-xs text-muted-foreground">{t.account_count} accounts</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Pre-populates your chart of accounts for your industry
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewWsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateWorkspace} disabled={!newWsName.trim()}>
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Global Search Dialog (placeholder) */}
      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Quick Search</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input 
                placeholder="Search transactions, contacts, accounts..." 
                className="pl-10"
                autoFocus
              />
            </div>
            <div className="text-sm text-muted-foreground text-center py-8">
              Start typing to search across your workspace
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* AI Buck Global Assistant — FAB removed 2026-02 because the bottom-right
          chat bubble overlapped report tables. AI Buck is still accessible via
          the dedicated /cfo-buck route and from in-app suggestion entry points. */}
      {/* <AIBuckGlobal /> */}

      {/* First-time setup wizard (auto-opens for brand-new workspaces) */}
      <SetupWizard />
    </div>
  );
}
